import React, {useEffect, useMemo, useState} from 'react';
import Modal from '../../../../../components-new/ui/Modal';
import Translate, {t} from '../../../../../components/Translate';
import config from "../../../../../config";
import {generalModalStyles} from "../../../../../utils/General";
import FileUpload from "../../../../../components-new/general/FileUpload";
import Input from "../../../../../components-new/form/Input";
import DropdownSelect from "../../../../../components-new/form/DropdownSelect";
import LoadingComponent from "../../../../../components-new/ui/LoadingComponent";
import colors from "../../../../../utils/Colors";
import ContactAvatar from "../../../../../components-new/general/ContactAvatar";
import SilverButton from "../../../../../components-new/buttons/SilverButton";
import BlueButton from "../../../../../components-new/buttons/BlueButton";
import {createDepartment, updateDepartment} from "../../../../../services/internalGroups/api";
import {GroupRole} from "../../../../../services/internalGroups/constants";
import uploadFileApi from "../../../../../utils/FileService";
import Field from "../../../../../components-new/form/Field";

Modal.setAppElement('#root');

const DepartmentModal = ({
                             company,
                             user,
                             users,
                             isOpen,
                             addNotification,
                             type, // create,update
                             closeModal,
                             isFetchingUsers,
                             onSuccess,
                             department
                         }) => {

    const [loading, setLoading] = useState(false);
    const [localFile, setLocalFile] = useState(null);

    const [form, setForm] = useState({
        thumbUrl: null,
        name: "",
        description: "",
        headId: null
    });

    useEffect(() => {
        setLocalFile(null);
        if (type === 'update' && department) {
            const head = department.members ? department.members.find(m => m.groupRole === GroupRole.DEPARTMENT_HEAD) : null;
            setForm({
                thumbUrl: department.thumbUrl || null,
                name: department.name || "",
                description: department.description || "",
                headId: head ? head.id : null
            });
        } else {
            setForm({
                thumbUrl: null,
                name: "",
                description: "",
                headId: null
            });
        }
    }, [type, department, isOpen]);

    const handleCreate = async () => {
        setLoading(true);

        let uploadedUrl = form.thumbUrl;

        if (localFile instanceof File) {
            try {
                const formData = new FormData();
                formData.append('file', localFile);
                uploadedUrl = await uploadFileApi(formData);
            } catch (e) {
                addNotification(t({text: 'somethingWentWrong', user}), config.notificationLevels.error);
                setLoading(false);
                return;
            }
        }

        createDepartment(company.id, {...form, thumbUrl: uploadedUrl}).then(response => {
            if (response.success) {
                onSuccess(response.department);
            }
        })
            .catch(e => {
                const response = e.response.data
                addNotification(t({text: 'error.' + response.message, user}), config.notificationLevels.error)
            })
            .finally(() => setLoading(false));
    }

    const handleUpdate = async () => {
        setLoading(true);

        let uploadedUrl = form.thumbUrl;

        if (localFile instanceof File) {
            try {
                const formData = new FormData();
                formData.append('file', localFile);
                uploadedUrl = await uploadFileApi(formData);
            } catch (e) {
                addNotification(t({text: 'somethingWentWrong', user}), config.notificationLevels.error);
                setLoading(false);
                return;
            }
        }

        updateDepartment(department.id, company.id, {...form, thumbUrl: uploadedUrl}).then(response => {
            if (response.success) {
                onSuccess(response.department);
            }
        })
            .catch(e => {
                const response = e.response.data
                addNotification(t({text: 'error.' + response.message, user}), config.notificationLevels.error)
            })
            .finally(() => setLoading(false));
    }

    const computedOptions = useMemo(() => {
        if (!users) return []
        if (type === 'create') {
            return users.filter(u => !u.memberships || u.memberships.length === 0)
        }
        if (!department) return []
        return users.filter(u => !u.memberships || u.memberships.length === 0 || u.memberships.filter(m => m.groupId !== department.id).length === 0)
    }, [users, type, department])

    return (
        <Modal closeTimeoutMS={config.closeTimeoutMS}
               isOpen={isOpen}
               style={{
                   ...generalModalStyles.standardModalStyles,
                   content: {
                       ...generalModalStyles.standardModalStyles.content,
                       maxWidth: '600px',
                       width: '90%',
                   }
               }}
               onRequestClose={closeModal}
               title={<Translate text={type === 'create' ? 'addDepartment' : 'updateDepartment'}/>}
               headerSeparator
               footerSeparator
               footer={<React.Fragment>
                   <SilverButton type="button" width="auto" height="40px" onClick={closeModal}>
                       <Translate text="cancel"/>
                   </SilverButton>
                   <BlueButton type="button" width="auto" height="40px"
                               onClick={type === 'create' ? handleCreate : handleUpdate}
                               disabled={!(form.name.trim())}>
                       {loading ? <LoadingComponent size={24} stroke={'#fff'}/> : type === 'create' ?
                           <Translate text="create"/> : <Translate text="update"/>}
                   </BlueButton>
               </React.Fragment>}
        >
            <div className="internal-grouping__modal-form">
                    <div className="internal-grouping__upload">
                    <FileUpload
                        windowSize={120}
                        imgSize={110}
                        svgSize={50}
                        progressSize={120}
                        uploadOnSelect={false}
                        handleChange={(v) => setLocalFile(v)}
                        handleDelete={() => {
                            setLocalFile(null);
                            setForm({...form, thumbUrl: null});
                        }}
                        image={
                            localFile instanceof File
                                ? URL.createObjectURL(localFile)
                                : form.thumbUrl
                        }
                        style={{minWidth: 0, flexShrink: 0}}
                    />
                    </div>

                    <Input
                        label={<Translate text="departmentName"/>}
                        required
                        value={form.name}
                        onChange={(e) => setForm({...form, name: e.target.value})}
                        placeholder={t({user, text: "departmentName"})}
                        maxLength={256}
                    />

                    <Input
                        label={<Translate text="description"/>}
                        value={form.description}
                        onChange={(e) => setForm({...form, description: e.target.value})}
                        isTextarea={true}
                        resize={"none"}
                        placeholder={t({user, text: "description"})}
                        maxLength={1024}
                        style={{
                            height: '120px'
                        }}
                    />

                    <Field label="selectDepartmentHead">
                    <DropdownSelect
                        selectedValue={
                            users ? [users.find(u => u.id === form.headId)].filter(v => !!v).map(u => ({
                                    ...u,
                                    value: u.id
                                }))
                                :
                                null
                        }
                        onChange={(e) => {
                            setForm({...form, headId: form.headId === e.id ? null : e.id});
                        }}
                        options={computedOptions.map(u => ({...u, value: u.id}))}
                        searchBy={computedOptions.length > 5 ?
                            (option, search) => {
                                return !search || (option.firstName + ' ' + option.lastName).toLowerCase().includes(search.toLowerCase())
                            }
                            :
                            null
                        }
                        renderLabel={
                            u => (u && u.length > 0) ?
                                <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                    <ContactAvatar
                                        contact={{
                                            firstName: u[0].firstName,
                                            lastName: u[0].lastName,
                                            thumbUrl: u[0].thumbUrl,
                                        }}
                                        variant="menu"
                                    />
                                    <span>{u[0].firstName + ' ' + u[0].lastName}</span>
                                </div>
                                :
                                isFetchingUsers ?
                                    <LoadingComponent/>
                                    :
                                    <span style={{color: colors.silverDark}}>
                                        <Translate text={'selectDepartmentHead'}/>
                                    </span>
                        }
                        variant={'outline'}
                        styles={{
                            width: '100%',
                            height: '36px',
                        }}
                        direction={'top'}
                        closeOnOptionSelect={true}
                        classes="message-search-timeline-dropdown"
                    >
                        {option =>
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                <ContactAvatar
                                    contact={{
                                        firstName: option.firstName,
                                        lastName: option.lastName,
                                        thumbUrl: option.thumbUrl,
                                    }}
                                    variant="menu"
                                />
                                <span>{option.firstName + ' ' + option.lastName}</span>
                            </div>
                        }
                    </DropdownSelect>
                    </Field>
            </div>
        </Modal>
    )
        ;
};

export default DepartmentModal;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/internal-grouping/components/DepartmentModal.jsx